import React from 'react';
import type { OrgLaunchSettings } from '@bb/automaton';
import getOrgLaunchSetting from 'api/OrgController/getOrgLaunchSetting';
import { useDialogStore } from 'hooks/dialogStore/useDialogStore';
import { LEGACY_DIALOG_KEYS } from 'modals/keys';
import AuthContext from '../AuthContext';
import LayoutContext from '../LayoutContext';
import OnboardingPreLaunchModalContext from './OnboardingPreLaunchModalContext';

export type SetupBrokerageModalContextType = {
  visible: boolean;
  openModal: () => void;
  closeModal: (shouldOpenPreLaunchModal?: boolean) => void;
  launchSetting: OrgLaunchSettings | undefined;
};

const SetupBrokerageWizardModalContext =
  React.createContext<SetupBrokerageModalContextType>({
    visible: false,
    openModal: () => null,
    closeModal: () => null,
    launchSetting: undefined,
  });

export const SetupBrokerageWizardModalContextProvider: React.FC = ({
  children,
}) => {
  const { auth } = React.useContext(AuthContext);
  const { addDialog, removeDialog, isDialogVisible, isUsingNewArchitecture } =
    useDialogStore();
  const usingNewModalArch = isUsingNewArchitecture(
    LEGACY_DIALOG_KEYS.SetupBrokerageWizardModal,
  );

  const { openModal: openPreLaunchModal } = React.useContext(
    OnboardingPreLaunchModalContext,
  );

  const [visible, setVisible] = React.useState(false);

  const [launchSetting, setLaunchSetting] = React.useState<OrgLaunchSettings>();

  React.useEffect(() => {
    const fetchLaunchSetting = async () => {
      const data = await getOrgLaunchSetting();
      if (data) setLaunchSetting(data);
    };

    if (auth && auth.org.type === 'brokerage' && auth.org.onboardingPortalId) {
      fetchLaunchSetting();
    }
  }, [auth]);

  const { setChatVisibility } = React.useContext(LayoutContext);

  const openModal: SetupBrokerageModalContextType['openModal'] = () => {
    setChatVisibility(false);

    if (usingNewModalArch) {
      addDialog({ key: LEGACY_DIALOG_KEYS.SetupBrokerageWizardModal });
    } else {
      setVisible(true);
    }
  };

  const closeModal: SetupBrokerageModalContextType['closeModal'] =
    shouldOpenPreLaunchModal => {
      setChatVisibility(true);
      if (usingNewModalArch) {
        removeDialog(LEGACY_DIALOG_KEYS.SetupBrokerageWizardModal);
      } else {
        setVisible(false);
      }
      if (shouldOpenPreLaunchModal) openPreLaunchModal();
    };

  return (
    <SetupBrokerageWizardModalContext.Provider
      value={{
        visible: usingNewModalArch
          ? isDialogVisible(LEGACY_DIALOG_KEYS.SetupBrokerageWizardModal)
          : visible,
        openModal,
        closeModal,
        launchSetting,
      }}
    >
      {children}
    </SetupBrokerageWizardModalContext.Provider>
  );
};

export default SetupBrokerageWizardModalContext;
